/* eslint-disable max-nested-callbacks */
import { useMutation } from '@apollo/client';
import { gql } from 'graphql-tag';
import { useCallback } from 'react';
import { ResourceRequestStatus } from '~/types';
import specificResourceAllocationFragment from '~/modules/resourcing/common/fragments/specificResourceAllocationFragment';
import {
  tryResourcePlanAllocatedUsersAndRolesCachedQuery,
  GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES
} from '~/modules/projects/resourcing-plan/ResourceRequestChart/hooks/useFetchAllocatedUsersAndRoles';
import { GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES_VARIABLES } from '~/modules/projects/resourcing-plan/ResourceRequestChart/hooks/useFetchResourcePlanUsersAndRoles';
import { getSessionStorage } from '~/modules/common/hooks';
import { getUpdatedAllocatedUserListFromAllocations } from '~/modules/resourcing/common/util/allocationCacheUpdates';

export const BULK_ACCEPT_ALLOCATIONS_FOR_RESOURCE_REQUEST = gql`
  mutation BulkAcceptAllocationsForResourceRequest(
    $input: BulkAcceptAllocationsForResourceRequestInput!
  ) {
    bulkAcceptAllocationsForResourceRequest(input: $input) {
      acceptedResourceRequestAndAllocationsEntry {
        resourceRequest {
          id
          requestStatus
        }
        resourceAllocations {
          ...SpecificResourceAllocation
        }
      }
      isOptimistic @client
    }
  }
  ${specificResourceAllocationFragment}
`;

export const updateCachedAllocationDataOnResourcePlanPage = ({
  proxy,
  allocationChartRef,
  resourceAllocations
}) => {
  const newUsers = resourceAllocations.map(allocation => allocation.user);

  const variables = getSessionStorage(
    GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES_VARIABLES,
    null
  );

  const cachedResults = tryResourcePlanAllocatedUsersAndRolesCachedQuery({
    proxy,
    variables
  });

  const userList = cachedResults
    ? getUpdatedAllocatedUserListFromAllocations({
        newAllocations: resourceAllocations,
        cachedAllocatedUsers:
          cachedResults.project.resourcePlanAllocatedUsersAndRoles
      })
    : [];

  proxy.writeQuery({
    query: GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES,
    variables,
    data: {
      project: {
        ...cachedResults?.project,
        resourcePlanAllocatedUsersAndRoles: userList
      }
    }
  });

  allocationChartRef?.current?.handleResourceAllocationUserChange({
    newUsers: newUsers.map(user => user.user)
  });
  allocationChartRef?.current?.handleLoadingStateOnResourceRequestAccept({
    newUsers
  });
};

export const updateCacheForProjectResourcePlanPage = ({
  allocationChartRef
}) => (proxy, mutationResponse) => {
  const {
    data: {
      bulkAcceptAllocationsForResourceRequest: {
        isOptimistic,
        acceptedResourceRequestAndAllocationsEntry
      }
    }
  } = mutationResponse;

  if (
    isOptimistic ||
    !acceptedResourceRequestAndAllocationsEntry ||
    acceptedResourceRequestAndAllocationsEntry.length === 0
  )
    return;

  updateCachedAllocationDataOnResourcePlanPage({
    proxy,
    allocationChartRef,
    resourceAllocations: acceptedResourceRequestAndAllocationsEntry.flatMap(
      entry => entry.resourceAllocations || []
    )
  });
};

export const useBulkAcceptAllocationsForResourceRequest = () => {
  const [
    bulkAcceptAllocationsForResourceRequest,
    { loading, error }
  ] = useMutation(BULK_ACCEPT_ALLOCATIONS_FOR_RESOURCE_REQUEST);

  const bulkAcceptWithCacheUpdate = useCallback(
    ({ acceptAllocationInput, allocationChartRef }) => {
      const optimisticResponse = {
        __typename: 'Mutation',
        bulkAcceptAllocationsForResourceRequest: {
          __typename: 'BulkAcceptAllocationsForResourceRequestResult',
          isOptimistic: true,
          acceptedResourceRequestAndAllocationsEntry: acceptAllocationInput.map(
            req => ({
              __typename: 'AcceptedResourceRequestAndAllocationsEntry',
              resourceRequest: {
                __typename: 'ResourceRequest',
                id: req.resourceRequestId,
                requestStatus: ResourceRequestStatus.Complete
              },
              resourceAllocations: []
            })
          )
        }
      };

      return bulkAcceptAllocationsForResourceRequest({
        variables: {
          input: { acceptAllocationInput }
        },
        refetchQueries: ['resourceRequestWithTimeoff'],
        optimisticResponse,
        update: updateCacheForProjectResourcePlanPage({ allocationChartRef })
      });
    },
    [bulkAcceptAllocationsForResourceRequest]
  );

  return {
    bulkAcceptAllocationsForResourceRequest: bulkAcceptWithCacheUpdate,
    isLoading: loading,
    error
  };
};

export default useBulkAcceptAllocationsForResourceRequest;
